import { useContext, useState } from 'react';

import {
  ChainlitContext,
  useChatInteract,
  useChatMessages,
  useChatSession,
  useConfig
} from '@chainlit/react-client';

import { Markdown } from '@/components/Markdown';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Check, ChevronDown } from 'lucide-react';

import { NewChatDialog } from './NewChat';

interface Props {
  navigate?: (to: string) => void;
}

export default function ChatProfiles({ navigate }: Props) {
  const apiClient = useContext(ChainlitContext);
  const { config } = useConfig();
  const { chatProfile, setChatProfile } = useChatSession();
  const { firstInteraction } = useChatMessages();
  const { clear } = useChatInteract();
  const [newChatProfile, setNewChatProfile] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [open, setOpen] = useState(false);

  // Early return check to prevent unnecessary renders and resource waste
  if (!config?.chatProfiles?.length || config.chatProfiles.length <= 1) {
    return null;
  }

  // Handle case when no profile is selected
  // useEffect(() => {
  //   if (!chatProfile) {
  //     setChatProfile(config.chatProfiles[0].name);
  //   }
  // }, [chatProfile, config.chatProfiles, setChatProfile]);

  // // Handle case when selected profile becomes invalid
  // useEffect(() => {
  //   if (chatProfile) {
  //     const profileExists = config.chatProfiles.some(
  //       (profile) => profile.name === chatProfile
  //     );
  //     if (!profileExists) {
  //       setChatProfile(config.chatProfiles[0].name);
  //     }
  //   }
  // }, [chatProfile, config.chatProfiles, setChatProfile]);

  const handleClose = () => {
    setOpenDialog(false);
    setNewChatProfile(null);
    navigate?.('/');
  };

  const handleConfirm = (profile: string) => {
    setChatProfile(profile);
    setNewChatProfile(null);
    clear();
    handleClose();
    setOpen(false);
  };

  const handleSelectProfile = (profileName: string) => {
    // Si c'est déjà le profil actuel, fermer le popover
    if (profileName === chatProfile) {
      setOpen(false);
      return;
    }

    setNewChatProfile(profileName);
    
    // Si une conversation est en cours, demander confirmation
    if (firstInteraction) {
      setOpenDialog(true);
      setOpen(false);
    } else {
      handleConfirm(profileName);
    }
  };

  const allowHtml = config?.features?.unsafe_allow_html;
  const latex = config?.features?.latex;

  // Profil actuellement sélectionné
  const currentProfile = config.chatProfiles.find(p => p.name === chatProfile);
  const currentIcon = currentProfile?.icon?.includes('/public')
    ? apiClient.buildEndpoint(currentProfile.icon)
    : currentProfile?.icon;

  return (
    <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            id="chat-profiles"
            className="w-fit justify-between border-none bg-transparent text-muted-foreground font-semibold text-lg hover:bg-accent gap-2"
          >
            {currentProfile ? (
              <div className="flex items-center gap-2">
                {currentIcon && (
                  <img
                    src={currentIcon}
                    alt={currentProfile.display_name || currentProfile.name}
                    className="w-6 h-6 rounded-md object-cover"
                  />
                )}
                <span>{currentProfile.display_name || currentProfile.name}</span>
              </div>
            ) : (
              <span>Sélectionner un agent</span>
            )}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[800px] p-0" 
          align="start"
          side="bottom"
          sideOffset={8}
        >
          <div className="p-4 border-b">
            <h3 className="font-semibold text-lg">Choisissez votre agent</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Sélectionnez l'agent spécialisé qui correspond le mieux à vos besoins
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 p-4 max-h-[600px] overflow-y-auto">
            {config.chatProfiles.map((profile) => {
              const icon = profile.icon?.includes('/public')
                ? apiClient.buildEndpoint(profile.icon)
                : profile.icon;
              
              const isSelected = profile.name === chatProfile;

              return (
                <button
                  key={profile.name}
                  data-test={`select-item:${profile.name}`}
                  onClick={() => handleSelectProfile(profile.name)}
                  className={`
                    relative flex flex-col p-4 rounded-lg border-2 text-left transition-all
                    hover:border-primary hover:shadow-md
                    ${isSelected ? 'border-primary bg-accent' : 'border-border'}
                  `}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 bg-primary rounded-full p-1">
                      <Check className="w-3 h-3 text-primary-foreground" />
                    </div>
                  )}
                  
                  <div className="flex items-start gap-3 mb-3">
                    {icon && (
                      <img
                        src={icon}
                        alt={profile.display_name || profile.name}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-base truncate">
                        {profile.display_name || profile.name}
                      </h4>
                      {profile.default && (
                        <span className="text-xs text-muted-foreground">
                          Par défaut
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-sm text-muted-foreground line-clamp-3 mb-3">
                    <Markdown allowHtml={allowHtml} latex={latex}>
                      {profile.markdown_description}
                    </Markdown>
                  </div>

                  {profile.starters && profile.starters.length > 0 && (
                    <div className="mt-auto pt-3 border-t">
                      <p className="text-xs font-semibold text-muted-foreground mb-2">
                        Suggestions
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {profile.starters.slice(0, 2).map((starter, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-1 bg-muted rounded-md truncate"
                          >
                            {starter.icon && <span className="mr-1">{starter.icon}</span>}
                            {starter.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      
      <NewChatDialog
        open={openDialog}
        handleClose={handleClose}
        handleConfirm={() => newChatProfile && handleConfirm(newChatProfile)}
      />
    </div>
  );
}